import React, { ChangeEvent, useState } from 'react';
import { Box, Button, Typography, Divider, Container, TextField, ToggleButton, ToggleButtonGroup, MenuItem } from '@mui/material';
import { CloudUpload, Movie, Image as ImageIcon, Send, Link as LinkIcon } from '@mui/icons-material';

interface MediaUploaderProps {
    preview: string | null;
    fileType: string | null;
    onImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onClearMedia: () => void;
    onSubmitMedia?: (payload: { mediaUrl: string | null; lifetimeMinutes: number | null }) => void;
    poppins: { fontFamily: string };
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
                                                         preview,
                                                         fileType,
                                                         onImageChange,
                                                         onClearMedia,
                                                         onSubmitMedia,
                                                         poppins
                                                     }) => {
    // Mode switcher: 'file' or 'url'
    const [uploadMode, setUploadMode] = useState<'file' | 'url'>('file');
    const [externalUrl, setExternalUrl] = useState<string>('');

    // Online duration / expiry selection (in minutes)
    const [lifetime, setLifetime] = useState<number | string>('infinite');

    const isVideo = fileType?.startsWith('video/') || externalUrl.match(/\.(mp4|webm|mov)($|\?)/i);

    const handleModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'file' | 'url' | null) => {
        if (newMode !== null) {
            setUploadMode(newMode);
            // Clear current states on switch to avoid sending mixed payloads
            onClearMedia();
            setExternalUrl('');
        }
    };

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setExternalUrl(e.target.value);
    };

    const handleFormSubmit = () => {
        if (onSubmitMedia) {
            const minutes = lifetime === 'infinite' ? null : Number(lifetime);
            onSubmitMedia({
                mediaUrl: uploadMode === 'url' ? externalUrl : null,
                lifetimeMinutes: minutes
            });
        }
    };

    return (
        <Container maxWidth="lg" sx={{ borderRadius: '10px', boxShadow: '0 10px 40px rgba(0,0,0,0.03)', mb: 10, pt: 5, pb: 20, bgcolor: 'white' }}>

            {/* Input Selection Mode Header */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <ToggleButtonGroup
                    value={uploadMode}
                    exclusive
                    onChange={handleModeChange}
                    size="small"
                    sx={{ bgcolor: '#F4F7FE', borderRadius: '12px', p: 0.5, '& .MuiToggleButton-root': { border: 'none', borderRadius: '10px', px: 3, fontWeight: 600, ...poppins } }}
                >
                    <ToggleButton value="file" sx={{ '&.Mui-selected': { bgcolor: 'orange', color: 'white', '&:hover': { bgcolor: 'orange' } } }}>
                        Local File
                    </ToggleButton>
                    <ToggleButton value="url" sx={{ '&.Mui-selected': { bgcolor: 'orange', color: 'white', '&:hover': { bgcolor: 'orange' } } }}>
                        Media URL
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Main Handling Frame Box */}
            <Box sx={{
                border: '2px dashed #E0E5F2',
                borderRadius: '20px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                bgcolor: '#F4F7FE',
                minHeight: 280,
                transition: 'all 0.3s ease',
                '&:hover': { borderColor: 'orange', bgcolor: '#FFF' }
            }}>
                {/* --- MODE A: FILE PREVIEW LOGIC SPLITS --- */}
                {uploadMode === 'file' ? (
                    !preview ? (
                        <Button
                            variant="text"
                            component="label"
                            startIcon={<CloudUpload />}
                            sx={{ ...poppins, textTransform: 'none', color: '#707EAE', fontWeight: 600, flexDirection: 'column', gap: 1 }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Upload Media Asset
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#A3AED0' }}>
                                (Images, GIFs, or Videos)
                            </Typography>
                            <input
                                type="file"
                                hidden
                                accept="image/*,video/*"
                                onChange={onImageChange}
                            />
                        </Button>
                    ) : (
                        <Box textAlign="center" sx={{ width: '100%' }}>
                            <Box sx={{ mb: 2 }}>
                                {isVideo ? (
                                    <video
                                        src={preview}
                                        controls
                                        style={{ width: '100%', maxHeight: 350, borderRadius: '16px', objectFit: 'contain', backgroundColor: '#000' }}
                                    />
                                ) : (
                                    <img
                                        src={preview}
                                        alt="preview"
                                        style={{ width: '100%', maxHeight: 220, borderRadius: '16px', objectFit: 'contain' }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                                {isVideo ? <Movie fontSize="small" color="action" /> : <ImageIcon fontSize="small" color="action" />}
                                <Button size="small" color="error" variant="text" onClick={onClearMedia} sx={{ fontWeight: 600, ...poppins }}>
                                    Remove Media
                                </Button>
                            </Box>
                        </Box>
                    )
                ) : (
                    /* --- MODE B: URL INPUT & PREVIEW LOGIC SPLITS --- */
                    <Box sx={{ width: '100%', p: 1 }}>
                        <TextField
                            fullWidth
                            label="Paste Media Asset URL Link"
                            variant="outlined"
                            value={externalUrl}
                            onChange={handleUrlChange}
                            placeholder="https://example.com/image.jpg"
                            InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white', ...poppins } }}
                            sx={{ mb: 3 }}
                        />
                        {externalUrl && (
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#A3AED0', display: 'block', mb: 1, ...poppins }}>Media Live Preview Link Window</Typography>
                                {isVideo ? (
                                    <video src={externalUrl} controls style={{ width: '100%', maxHeight: 220, borderRadius: '16px', backgroundColor: '#000' }} />
                                ) : (
                                    <img src={externalUrl} alt="External Live Preview" style={{ width: '100%', maxHeight: 220, borderRadius: '16px', objectFit: 'contain' }} onError={(e)=>{ (e.target as HTMLImageElement).style.display = 'none'; }} />
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {/* --- UNIVERSAL LIFETIME DROPDOWN SELECTION SECTION --- */}
                {(preview || externalUrl) && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

                        <TextField
                            select
                            fullWidth
                            label="Online Retention Lifetime Window"
                            value={lifetime}
                            onChange={(e) => setLifetime(e.target.value)}
                            InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white', ...poppins } }}
                            sx={{ mb: 3 }}
                        >
                            <MenuItem value="infinite">Keep Online Indefinitely (No Deletion)</MenuItem>
                            <MenuItem value={15}>15 Minutes</MenuItem>
                            <MenuItem value={60}>1 Hour</MenuItem>
                            <MenuItem value={1440}>24 Hours (1 Day)</MenuItem>
                            <MenuItem value={10080}>7 Days (1 Week)</MenuItem>
                        </TextField>

                        {/* Dedicated Submit Action Button */}
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<Send />}
                            onClick={handleFormSubmit}
                            sx={{
                                bgcolor: 'orange',
                                color: 'white',
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                py: 1.5,
                                fontSize: '0.95rem',
                                '&:hover': { bgcolor: '#E28700' },
                                ...poppins
                            }}
                        >
                            Confirm & Stage Asset
                        </Button>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default MediaUploader;